import { useState } from 'react';
import { hoursToMilliseconds } from 'date-fns';
import { ProgressBar, type ProgressBarVariant } from '@/shared/components/atoms/ProgressBar';
import { ExportCsvButton } from '@/shared/components/ExportCSVButton';
import Button from '@/shared/components/flip-ui-kit/Button';
import Card from '@/shared/components/flip-ui-kit/Card';
import { type PillProps } from '@/shared/components/flip-ui-kit/Pill';
import { FundingAndRedemptionsTable } from '@/shared/components/FundingAndRedemptionsTable';
import { TabPanel } from '@/shared/components/molecules/TabPanel';
import WalletActionButton from '@/shared/components/WalletActionButton';
import {
  useExecuteRedemption,
  usePendingRedemption,
  useRedemptionDelay,
} from '@/shared/hooks/funding';
import useBoolean from '@/shared/hooks/useBoolean';
import useDelegateFlip from '@/shared/hooks/useDelegateFlip';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useInterval from '@/shared/hooks/useInterval';
import useToast from '@/shared/hooks/useToast';
import CheckCircleIcon from '@/shared/icons/flip-ui-kit/small/CheckCircle';
import ClockIcon from '@/shared/icons/flip-ui-kit/small/Clock';
import SleepClockIcon from '@/shared/icons/flip-ui-kit/small/SleepClock';
import { formatTimeUntilExpiry } from '@/shared/utils/date';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { getViemError } from '@/shared/utils/functions';
import { clamp } from '@/shared/utils/numbers';
import { DelegateActivityTable } from './DelegateActivityTable';
import { DelegationHistoryTable } from './DelegationHistoryTable';
import ExportHistoryModal from './ExportHistoryModal';
import UndelegateModal from './UndelegateModal';
import DelegatorRedemptionModal from '../RedemptionModal/DelegatorRedemptionModal';

const WithdrawableBalanceCard = ({
  openRedeemModal,
  goToDelegate,
}: {
  openRedeemModal: VoidFunction;
  goToDelegate: VoidFunction;
}) => {
  const { refresh, nextEpoch, redeemableBalance, currentEpoch } = useEvmScAccount(true);
  const { data: delay = hoursToMilliseconds(48) } = useRedemptionDelay();
  const toast = useToast('withdrawal-card');
  const { idSs58: delegatorIdSs58 } = useEvmScAccount(true);
  const { data: redemption = null, refetch } = usePendingRedemption(delegatorIdSs58);

  const [time, setTime] = useState(Date.now());

  useInterval(() => {
    setTime(Date.now());
  }, 1000);

  const { mutate: executeWithdrawal, isPending } = useExecuteRedemption({
    onSuccess: () => {
      toast.success('Withdrawal request submitted successfully');
      refetch();
    },
    onError: (error) => {
      toast.error('Failed to submit withdrawal request', {
        description: getViemError(error),
      });
    },
  });

  const { mutate: delegateFlip, isPending: delegatePending } = useDelegateFlip({
    onSuccess: refresh,
  });

  let withdrawButtonText;
  let withdrawButtonDisabled;
  let withdrawButtonAction;
  let withdrawButtonConnectionRequired = false;
  let pct = 0;
  let variant: ProgressBarVariant = 'standard';
  let info;
  let pillProps: PillProps | undefined;

  if (redemption) {
    const requestedAt = redemption.startTime - delay;
    const isExpired = redemption.expiryTime < time;
    const isReady = redemption.startTime < time;
    pct = clamp(((time - requestedAt) / (redemption.expiryTime - requestedAt)) * 100, 0, 100);
    withdrawButtonDisabled = !isReady;
    withdrawButtonAction = () => executeWithdrawal(delegatorIdSs58);
    withdrawButtonText = 'Submit withdrawal';
    withdrawButtonConnectionRequired = true;

    if (isExpired) {
      variant = 'error';
      info = `Withdrawal expired at ${new Date(redemption.expiryTime).toLocaleString()}`;
    } else if (isReady) {
      variant = pct >= 90 ? 'warning' : 'active';
      info = (
        <>
          <SleepClockIcon /> Claim will expire in{' '}
          {formatTimeUntilExpiry((redemption.expiryTime - time) / 1000)}
        </>
      );
      pillProps = {
        text: 'Ready for Withdrawal',
        Icon: ClockIcon,
        color: 'green',
      };
    } else {
      variant = 'standard';
      info = (
        <>
          <span className="text-cf-light-2">Claimable in:</span>
          {formatTimeUntilExpiry((redemption.startTime - time) / 1000)}
        </>
      );
      pillProps = {
        text: 'Withdrawal Pending',
        Icon: ClockIcon,
        color: 'green',
      };
    }
  } else {
    info = redeemableBalance.gt(0) && (
      <>
        <CheckCircleIcon className="text-cf-green-1" /> Ready to start withdrawal
      </>
    );
    withdrawButtonText = 'Start withdrawal';
    withdrawButtonAction = openRedeemModal;
  }

  return (
    <Card
      title="Account Balance"
      tooltip="The amount of FLIP available to withdraw after undelegation. Once withdrawn, it returns to your Ethereum wallet."
      pillProps={pillProps && { ...pillProps, maxWidth: false }}
      footer={
        <div className="flex w-full items-center gap-x-4 pt-4">
          <WalletActionButton
            requireConnection
            chainId={chainflipAssetMap.Flip.chain.id}
            fullWidth
            onClick={() => {
              if (!nextEpoch.operatorIdSs58) {
                return goToDelegate();
              }

              if (nextEpoch.undelegatedBalance.gt(0)) {
                delegateFlip({ operator: nextEpoch.operatorIdSs58 });
              } else {
                toast.info('You have no undelegated balance to delegate');
              }
            }}
            loading={delegatePending && 'iconOnly'}
            disabled="ifLoading"
          >
            Delegate
          </WalletActionButton>
          <WalletActionButton
            requireConnection={withdrawButtonConnectionRequired}
            chainId={chainflipAssetMap.Flip.chain.id}
            fullWidth
            type="secondary-standard"
            disabled={withdrawButtonDisabled || 'ifLoading'}
            loading={isPending && 'iconOnly'}
            onClick={withdrawButtonAction}
          >
            {withdrawButtonText}
          </WalletActionButton>
        </div>
      }
    >
      <div className="flex items-center justify-between">
        {currentEpoch.undelegatedBalance.toFormatted()} {FLIP_SYMBOL}
        {info && (
          <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-white">
            {info}
          </div>
        )}
      </div>
      <ProgressBar value={pct} variant={variant} lightBackground />
    </Card>
  );
};

export default function ActivityView({ goToDelegate }: { goToDelegate: VoidFunction }) {
  const { currentEpoch, nextEpoch, idSs58: delegatorIdSs58 } = useEvmScAccount(true);
  const {
    value: redeemModalOpen,
    setTrue: openRedeemModal,
    setFalse: closeRedeemModal,
  } = useBoolean();
  const {
    value: undelegateModalOpen,
    setTrue: openUndelegateModal,
    setFalse: closeUndelegateModal,
  } = useBoolean();
  const {
    value: exportModalOpen,
    setTrue: openExportModal,
    setFalse: closeExportModal,
  } = useBoolean();
  const toast = useToast('activity-view');

  let pillProps: PillProps | undefined;

  if (!nextEpoch.delegatedBalance.eq(currentEpoch.delegatedBalance)) {
    const diff = nextEpoch.delegatedBalance.sub(currentEpoch.delegatedBalance);
    const sign = diff.gt(0) ? '+' : '-';
    let formattedAmount = diff.abs().toCapped();
    if (formattedAmount === '0.00') {
      formattedAmount = '<0.01';
    }

    pillProps = {
      text: `${sign}${formattedAmount} ${FLIP_SYMBOL}`,
      Icon: ClockIcon,
      color: 'green',
      maxWidth: false,
      tooltipContent: `Your delegated balance will ${diff.gt(0) ? 'increase' : 'decrease'} when the next epoch starts.`,
    };
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card
            title="Delegated Balance"
            tooltip="The amount of FLIP currently delegated to Operators. This balance is actively earning rewards."
            pillProps={pillProps}
            footer={
              <div className="flex w-full items-center gap-x-4 pt-4">
                <Button
                  type="secondary-standard"
                  fullWidth
                  onClick={() => {
                    if (nextEpoch.delegatedBalance.eq(0)) {
                      return toast.info('You have no delegated balance to undelegate');
                    }
                    openUndelegateModal();
                  }}
                >
                  Undelegate
                </Button>
              </div>
            }
          >
            {currentEpoch.delegatedBalance.toFormatted()} {FLIP_SYMBOL}
          </Card>
          <WithdrawableBalanceCard openRedeemModal={openRedeemModal} goToDelegate={goToDelegate} />
        </div>
        <div className="flex w-full flex-col">
          <TabPanel
            contentRight={
              <div className="flex items-center px-4">
                <ExportCsvButton
                  label="Export history"
                  onExport={openExportModal}
                  canExport={Boolean(delegatorIdSs58)}
                />
              </div>
            }
            nodes={[
              {
                name: 'Delegation Activity',
                content: <DelegateActivityTable />,
              },
              {
                name: 'Account Funding Activity',
                content: <FundingAndRedemptionsTable accountIds={[delegatorIdSs58]} />,
              },
              {
                name: 'Delegation History',
                content: <DelegationHistoryTable />,
              },
            ]}
          />
        </div>
      </div>
      <DelegatorRedemptionModal open={redeemModalOpen} onClose={closeRedeemModal} />
      <UndelegateModal open={undelegateModalOpen} onClose={closeUndelegateModal} />
      <ExportHistoryModal
        delegatorIdSs58={delegatorIdSs58}
        open={exportModalOpen}
        onClose={closeExportModal}
      />
    </>
  );
}
